import { useState } from 'react';
import { Button, Input, Label, Switch } from '@/components/ui';
import { useUserGradeCreateMutation } from '../hooks/useUserGradeMutation';

interface Props {
  onSuccess?: () => void;
}

/** 0~100(%) 입력 파싱: 빈 값=undefined(BE DEFAULT), 범위 밖=에러 메시지 반환. */
const parseRate = (
  raw: string,
  errorMsg: string,
): { value: number | undefined } | { error: string } => {
  if (raw.trim() === '') return { value: undefined };
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) return { error: errorMsg };
  return { value: parsed };
};

export const GradeCreateForm = ({ onSuccess }: Props) => {
  const mutation = useUserGradeCreateMutation();
  const [gradeCode, setGradeCode] = useState('');
  const [gradeName, setGradeName] = useState('');
  const [priority, setPriority] = useState('');
  const [discountRate, setDiscountRate] = useState('');
  const [mileageRate, setMileageRate] = useState('');
  // 무통장입금 허용: 현행 기본 = 허용(true). 기업 등급에서만 false 로 차단.
  const [allowBankTransfer, setAllowBankTransfer] = useState(true);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!gradeCode.trim() || !gradeName.trim() || !priority) {
      setError('등급 코드, 등급명, 우선순위는 필수입니다.');
      return;
    }
    // 할인율/추가 적립률은 선택값(미입력=BE DEFAULT 0). 입력 시 0~100 범위 검증.
    const discount = parseRate(discountRate, '할인율은 0~100 사이로 입력해 주세요.');
    if ('error' in discount) {
      setError(discount.error);
      return;
    }
    const mileage = parseRate(mileageRate, '추가 적립률은 0~100 사이로 입력해 주세요.');
    if ('error' in mileage) {
      setError(mileage.error);
      return;
    }
    mutation.mutate(
      {
        gradeCode: gradeCode.trim().toUpperCase(),
        gradeName: gradeName.trim(),
        priority: Number(priority),
        ...(discount.value !== undefined ? { discountRate: discount.value } : {}),
        ...(mileage.value !== undefined ? { mileageRate: mileage.value } : {}),
        allowBankTransfer,
      },
      {
        onSuccess: () => onSuccess?.(),
        onError: (err) => setError(err.message),
      },
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 rounded-xl border p-6"
      style={{ borderColor: 'var(--divider-strong)', background: 'var(--surface-0)' }}
      data-testid="grade-create-form"
    >
      <h2 className="text-base font-semibold" style={{ color: 'var(--content-strong)' }}>
        회원 등급 등록
      </h2>

      <div className="grid grid-cols-4 gap-4">
        <div className="space-y-1">
          <Label htmlFor="grade-code" required>등급 코드</Label>
          <Input
            id="grade-code"
            value={gradeCode}
            onChange={(e) => setGradeCode(e.target.value)}
            placeholder="PLATINUM"
            maxLength={30}
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="grade-name" required>등급명</Label>
          <Input
            id="grade-name"
            value={gradeName}
            onChange={(e) => setGradeName(e.target.value)}
            placeholder="플래티넘"
            maxLength={100}
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="grade-priority" required>우선순위</Label>
          <Input
            id="grade-priority"
            type="number"
            min={1}
            max={999}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            placeholder="5"
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="grade-discount-rate">할인율 (%)</Label>
          <Input
            id="grade-discount-rate"
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={discountRate}
            onChange={(e) => setDiscountRate(e.target.value)}
            placeholder="0"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="grade-mileage-rate">추가 적립률 (%)</Label>
          <Input
            id="grade-mileage-rate"
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={mileageRate}
            onChange={(e) => setMileageRate(e.target.value)}
            placeholder="0"
          />
        </div>
      </div>

      <div className="flex items-center justify-between rounded-lg border px-4 py-3"
        style={{ borderColor: 'var(--divider-soft)', background: 'var(--surface-1)' }}>
        <div className="space-y-0.5">
          <Label htmlFor="grade-allow-bank-transfer" className="cursor-pointer">
            무통장입금 허용
          </Label>
          <p className="text-xs" style={{ color: 'var(--content-muted)' }}>
            끄면 이 등급 회원은 체크아웃에서 무통장입금을 선택할 수 없습니다.
          </p>
        </div>
        <Switch
          id="grade-allow-bank-transfer"
          checked={allowBankTransfer}
          onCheckedChange={setAllowBankTransfer}
          aria-label="무통장입금 허용"
        />
      </div>

      {error && (
        <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? '등록 중...' : '등급 등록'}
        </Button>
      </div>
    </form>
  );
};
